import React, { Component } from "react";
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Input,
  Label,
  FormGroup,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  CustomInput,
} from "reactstrap";
import axios from "axios";
import { END_POINT } from "../../../constants/defaultValues";
import {
  NotificatioErrController,
  NotificatioSucController,
} from "../../../helpers/Utils";
import { injectIntl } from "react-intl";
import IntlMessages from "../../../helpers/IntlMessages";

/**
 * AutoReplySettings - Quản lý quy tắc trả lời tự động
 * Phase 4: Auto-Reply Rules
 */
class AutoReplySettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rules: [],
      pages: [],
      loading: true,
      showModal: false,
      editingRule: null,
      formData: {
        name: "",
        page_id: "",
        trigger_type: "keyword",
        trigger_value: "",
        response_value: "",
        priority: 0,
      },
      saving: false,
      testMessage: "",
      testResult: null,
    };
  }

  componentDidMount() {
    this.loadRules();
    this.loadPages();
  }

  loadPages = async () => {
    // Check permission: Facebook pages require pro_auto
    const { license } = this.props;
    if (!license) return;
    const ranks = { free: 0, basic: 1, pro: 2, pro_auto: 3, enterprise: 4 };
    const currentPackage = (license.purchased_package || "free").toLowerCase();
    const currentRank = ranks[currentPackage] || 0;
    if (currentRank < 3) return; // pro_auto = 3

    try {
      const user_id = localStorage.getItem("b");
      const response = await axios.get(
        `${END_POINT}/api/facebook/pages?user_id=${user_id}`,
      );
      this.setState({ pages: response.data.data || [] });
    } catch (err) {
      console.error("Error loading pages:", err);
    }
  };

  loadRules = async () => {
    try {
      this.setState({ loading: true });
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${END_POINT}/api/auto-reply`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      this.setState({ rules: response.data.data || [], loading: false });
    } catch (err) {
      console.error("Error loading rules:", err);
      NotificatioErrController(
        this.props.intl.formatMessage({
          id: "settings.autoreply.notify.load-error",
        }),
      );
      this.setState({ loading: false });
    }
  };

  openModal = (rule = null) => {
    if (rule) {
      // Parse trigger_value JSON back to comma-separated string
      let triggerStr = "";
      try {
        const arr = JSON.parse(rule.trigger_value);
        triggerStr = Array.isArray(arr) ? arr.join(", ") : "";
      } catch (e) {
        triggerStr = rule.trigger_value || "";
      }

      this.setState({
        showModal: true,
        editingRule: rule,
        formData: {
          name: rule.name,
          page_id: rule.page_id || "",
          trigger_type: rule.trigger_type,
          trigger_value: triggerStr,
          response_value: rule.response_value,
          priority: rule.priority || 0,
        },
      });
    } else {
      this.setState({
        showModal: true,
        editingRule: null,
        formData: {
          name: "",
          page_id: "",
          trigger_type: "keyword",
          trigger_value: "",
          response_value: "",
          priority: 0,
        },
      });
    }
  };

  closeModal = () => {
    this.setState({
      showModal: false,
      editingRule: null,
    });
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      formData: { ...prev.formData, [name]: value },
    }));
  };

  handleSave = async () => {
    const { formData, editingRule } = this.state;
    const { intl } = this.props;

    if (!formData.name || !formData.response_value) {
      NotificatioErrController(
        intl.formatMessage({ id: "settings.autoreply.notify.input-error" }),
      );
      return;
    }

    // Convert comma-separated keywords to JSON array
    const keywords = formData.trigger_value
      .split(",")
      .map((k) => k.trim())
      .filter((k) => k);

    const payload = {
      ...formData,
      trigger_value: JSON.stringify(keywords),
      page_id: formData.page_id || null,
    };

    try {
      this.setState({ saving: true });
      const token = localStorage.getItem("access_token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingRule) {
        await axios.put(
          `${END_POINT}/api/auto-reply/${editingRule.id}`,
          payload,
          config,
        );
        NotificatioSucController(
          intl.formatMessage({
            id: "settings.autoreply.notify.update-success",
          }),
        );
      } else {
        await axios.post(`${END_POINT}/api/auto-reply`, payload, config);
        NotificatioSucController(
          intl.formatMessage({
            id: "settings.autoreply.notify.create-success",
          }),
        );
      }

      this.closeModal();
      this.loadRules();
    } catch (err) {
      console.error("Error saving rule:", err);
      NotificatioErrController(
        err.response?.data?.error ||
          intl.formatMessage({ id: "settings.autoreply.notify.save-error" }),
      );
    } finally {
      this.setState({ saving: false });
    }
  };

  handleDelete = async (id) => {
    const { intl } = this.props;
    if (
      !window.confirm(
        intl.formatMessage({ id: "settings.autoreply.notify.delete-confirm" }),
      )
    )
      return;

    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${END_POINT}/api/auto-reply/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      NotificatioSucController(
        intl.formatMessage({ id: "settings.autoreply.notify.delete-success" }),
      );
      this.loadRules();
    } catch (err) {
      console.error("Error deleting rule:", err);
      NotificatioErrController(
        intl.formatMessage({ id: "settings.autoreply.notify.delete-error" }),
      );
    }
  };

  handleToggle = async (rule) => {
    const { intl } = this.props;
    try {
      const token = localStorage.getItem("access_token");
      await axios.put(
        `${END_POINT}/api/auto-reply/${rule.id}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      this.loadRules();
    } catch (err) {
      console.error("Error toggling rule:", err);
      NotificatioErrController(
        intl.formatMessage({ id: "settings.autoreply.notify.toggle-error" }),
      );
    }
  };

  handleTest = async () => {
    const { testMessage } = this.state;
    if (!testMessage) return;

    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${END_POINT}/api/auto-reply/test`,
        { message: testMessage },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      this.setState({ testResult: response.data.data });
    } catch (err) {
      console.error("Error testing:", err);
    }
  };

  getPageName = (pageId) => {
    const { intl } = this.props;
    const page = this.state.pages.find((p) => p.page_id === pageId);
    return page
      ? page.page_name
      : intl.formatMessage({ id: "settings.autoreply.modal.option.all-pages" });
  };

  render() {
    const {
      rules,
      pages,
      loading,
      showModal,
      formData,
      saving,
      editingRule,
      testMessage,
      testResult,
    } = this.state;
    const { intl } = this.props;

    return (
      <Card className="mb-4">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <CardTitle className="mb-0">
              <i className="simple-icon-energy mr-2" />
              <IntlMessages id="settings.autoreply.title" />
            </CardTitle>
            <Button color="primary" size="sm" onClick={() => this.openModal()}>
              <i className="simple-icon-plus mr-1" />
              <IntlMessages id="settings.autoreply.add" />
            </Button>
          </div>

          {/* Test Panel */}
          <Card className="mb-3 bg-light">
            <CardBody className="py-2">
              <Row className="align-items-center">
                <Col md={8}>
                  <Input
                    placeholder={intl.formatMessage({
                      id: "settings.autoreply.test.placeholder",
                    })}
                    value={testMessage}
                    onChange={(e) =>
                      this.setState({ testMessage: e.target.value })
                    }
                    bsSize="sm"
                  />
                </Col>
                <Col md={2}>
                  <Button
                    color="info"
                    size="sm"
                    block
                    onClick={this.handleTest}
                  >
                    <IntlMessages id="settings.autoreply.test.button" />
                  </Button>
                </Col>
                <Col md={2}>
                  {testResult && (
                    <Badge color={testResult.matched ? "success" : "secondary"}>
                      {testResult.matched ? (
                        <IntlMessages id="settings.autoreply.test.matched" />
                      ) : (
                        <IntlMessages id="settings.autoreply.test.no-match" />
                      )}
                    </Badge>
                  )}
                </Col>
              </Row>
              {testResult && testResult.matched && (
                <div className="mt-2 p-2 bg-white rounded">
                  <small className="text-muted">
                    <IntlMessages id="settings.autoreply.test.rule" />{" "}
                    {testResult.rule?.name}
                  </small>
                  <br />
                  <small>
                    <IntlMessages id="settings.autoreply.test.response" />{" "}
                    {testResult.response}
                  </small>
                </div>
              )}
            </CardBody>
          </Card>

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" />
            </div>
          ) : rules.length === 0 ? (
            <div className="text-center text-muted py-4">
              <i className="simple-icon-energy mb-2" style={{ fontSize: 40 }} />
              <p>
                <IntlMessages id="settings.autoreply.empty.title" />
              </p>
              <Button color="primary" onClick={() => this.openModal()}>
                <IntlMessages id="settings.autoreply.create-first" />
              </Button>
            </div>
          ) : (
            <Table responsive className="table-hover">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>
                    <IntlMessages id="settings.autoreply.col.enable" />
                  </th>
                  <th>
                    <IntlMessages id="settings.autoreply.col.name" />
                  </th>
                  <th>
                    <IntlMessages id="settings.autoreply.col.keyword" />
                  </th>
                  <th>
                    <IntlMessages id="settings.autoreply.col.response" />
                  </th>
                  <th>
                    <IntlMessages id="settings.autoreply.col.page" />
                  </th>
                  <th>
                    <IntlMessages id="settings.autoreply.col.match" />
                  </th>
                  <th className="text-right">
                    <IntlMessages id="settings.autoreply.col.action" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => {
                  let keywords = [];
                  try {
                    keywords = JSON.parse(r.trigger_value) || [];
                  } catch (e) {}

                  return (
                    <tr key={r.id} className={!r.is_active ? "text-muted" : ""}>
                      <td>
                        <CustomInput
                          type="switch"
                          id={`switch-${r.id}`}
                          checked={r.is_active}
                          onChange={() => this.handleToggle(r)}
                        />
                      </td>
                      <td className="font-weight-semibold">{r.name}</td>
                      <td>
                        {keywords.slice(0, 3).map((k, i) => (
                          <Badge key={i} color="light" className="mr-1">
                            {k}
                          </Badge>
                        ))}
                        {keywords.length > 3 && (
                          <Badge color="secondary">
                            +{keywords.length - 3}
                          </Badge>
                        )}
                      </td>
                      <td
                        style={{
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {r.response_value}
                      </td>
                      <td>
                        <small>{this.getPageName(r.page_id)}</small>
                      </td>
                      <td>
                        <Badge color="info">{r.match_count}</Badge>
                      </td>
                      <td className="text-right">
                        <Button
                          color="link"
                          size="sm"
                          onClick={() => this.openModal(r)}
                        >
                          <i className="simple-icon-pencil" />
                        </Button>
                        <Button
                          color="link"
                          size="sm"
                          className="text-danger"
                          onClick={() => this.handleDelete(r.id)}
                        >
                          <i className="simple-icon-trash" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}

          {/* Modal thêm/sửa */}
          <Modal isOpen={showModal} toggle={this.closeModal} size="lg">
            <ModalHeader toggle={this.closeModal}>
              {editingRule ? (
                <IntlMessages id="settings.autoreply.modal.title-edit" />
              ) : (
                <IntlMessages id="settings.autoreply.modal.title-add" />
              )}
            </ModalHeader>
            <ModalBody>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>
                      <IntlMessages id="settings.autoreply.modal.label.name" />
                    </Label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={this.handleInputChange}
                      placeholder={intl.formatMessage({
                        id: "settings.autoreply.modal.placeholder.name",
                      })}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>
                      <IntlMessages id="settings.autoreply.modal.label.page" />
                    </Label>
                    <Input
                      type="select"
                      name="page_id"
                      value={formData.page_id}
                      onChange={this.handleInputChange}
                    >
                      <option value="">
                        {intl.formatMessage({
                          id: "settings.autoreply.modal.option.all-pages",
                        })}
                      </option>
                      {pages.map((p) => (
                        <option key={p.page_id} value={p.page_id}>
                          {p.page_name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label>
                  <IntlMessages id="settings.autoreply.modal.label.trigger" />
                </Label>
                <Input
                  name="trigger_value"
                  value={formData.trigger_value}
                  onChange={this.handleInputChange}
                  placeholder={intl.formatMessage({
                    id: "settings.autoreply.modal.placeholder.trigger",
                  })}
                />
                <small className="text-muted">
                  <IntlMessages id="settings.autoreply.modal.help.trigger" />
                </small>
              </FormGroup>

              <FormGroup>
                <Label>
                  <IntlMessages id="settings.autoreply.modal.label.response" />
                </Label>
                <Input
                  type="textarea"
                  name="response_value"
                  value={formData.response_value}
                  onChange={this.handleInputChange}
                  rows={4}
                  placeholder={intl.formatMessage({
                    id: "settings.autoreply.modal.placeholder.response",
                  })}
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  <IntlMessages id="settings.autoreply.modal.label.priority" />
                </Label>
                <Input
                  type="number"
                  name="priority"
                  value={formData.priority}
                  onChange={this.handleInputChange}
                  min={0}
                  max={100}
                />
                <small className="text-muted">
                  <IntlMessages id="settings.autoreply.modal.help.priority" />
                </small>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={this.closeModal}>
                <IntlMessages id="settings.autoreply.modal.cancel" />
              </Button>
              <Button
                color="primary"
                onClick={this.handleSave}
                disabled={saving}
              >
                {saving && (
                  <span className="spinner-border spinner-border-sm mr-1" />
                )}
                {editingRule ? (
                  <IntlMessages id="settings.autoreply.modal.update" />
                ) : (
                  <IntlMessages id="settings.autoreply.modal.save" />
                )}
              </Button>
            </ModalFooter>
          </Modal>
        </CardBody>
      </Card>
    );
  }
}

export default injectIntl(AutoReplySettings);
